/**
 * /try inline result-card state and DOM rendering.
 *
 * WHAT THIS FILE OWNS:
 * - Session-only result-card store for the current /try page instance.
 * - Stable card ids, newest-first ordering, and DOM updates under #try-results.
 * - Ready-state open control mirrors homepage `.home-sample-card` (icon + "open").
 *
 * WHY THIS EXISTS:
 * - /try-ui.ts should orchestrate submit/ingest work, not mutate result-card DOM.
 * - Result cards are deliberately ephemeral; refresh persistence is product
 *   scope for a later plan, not hidden localStorage behavior here.
 *
 * @file src/client/try-result-card.ts
 */

import { GistPipelineStatusLabels } from '@gist-protocol/core/shared/gist-pipeline-status.js';

export type TryResultCardStatus =
  | 'pending'
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'error';

export interface TryResultCardState {
  id: string;
  status: TryResultCardStatus;
  statusMessage: string;
  title?: string;
  sourceLabel?: string;
  shareUrl?: string;
  errorMessage?: string;
}

export interface TryResultCardStore {
  cards: TryResultCardState[];
  nextCardNumber: number;
  activeCardId: string | null;
}

export interface CreateTryResultCardParams {
  title?: string;
  sourceLabel?: string;
  statusMessage?: string;
}

const ResultsDom = Object.freeze({
  HOST_ID: 'try-results',
  CARD_CLASS: 'try-result-card',
  STATUS_CLASS: 'try-result-status',
  OPEN_CLASS: 'try-result-open'
});

const ActiveStatuses = new Set<TryResultCardStatus>([
  'pending',
  'uploading',
  'processing'
]);

const TERMINAL_ANNOUNCEMENT_MS = 100;

const store: TryResultCardStore = {
  cards: [],
  nextCardNumber: 1,
  activeCardId: null
};

let terminalAnnouncementCardId: string | null = null;
let terminalAnnouncementTimerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Creates the active session card and prepends it in the DOM.
 */
export function createCard(params: CreateTryResultCardParams = {}): TryResultCardState {
  clearTerminalAnnouncement();

  const id = `try-result-${store.nextCardNumber}`;
  store.nextCardNumber += 1;

  const card: TryResultCardState = {
    id,
    status: 'pending',
    statusMessage: params.statusMessage ?? GistPipelineStatusLabels.LOADING,
    title: normalizeOptionalText(params.title),
    sourceLabel: normalizeOptionalText(params.sourceLabel)
  };

  store.cards.unshift(card);
  store.activeCardId = id;
  renderStore();
  return { ...card };
}

/**
 * Applies a state patch while keeping store ownership inside this module.
 */
export function updateCard(id: string | null | undefined, patch: Partial<TryResultCardState>): TryResultCardState | null {
  if (!id) {
    return null;
  }

  const index = store.cards.findIndex((card) => card.id === id);
  if (index < 0) {
    return null;
  }

  const previousState = store.cards[index];
  const nextState: TryResultCardState = {
    ...previousState,
    ...normalizePatch(patch),
    id: previousState.id
  };
  store.cards[index] = nextState;

  if (shouldAnnounceTerminalTransition(previousState, nextState)) {
    scheduleTerminalAnnouncement(id);
  }

  if (!ActiveStatuses.has(nextState.status) && store.activeCardId === id) {
    store.activeCardId = null;
  }

  renderStore();
  return { ...nextState };
}

/**
 * Settles a card with the share URL created by the submit flow.
 */
export function markReady(id: string | null | undefined, shareUrl: string): TryResultCardState | null {
  return updateCard(id, {
    status: 'ready',
    statusMessage: 'Ready',
    shareUrl
  });
}

/**
 * Settles a card with user-facing error copy from resolveTryErrorMessage().
 */
export function markError(id: string | null | undefined, message: string): TryResultCardState | null {
  return updateCard(id, {
    status: 'error',
    statusMessage: message,
    errorMessage: message
  });
}

export function getActiveCardId(): string | null {
  return store.activeCardId;
}

function normalizePatch(patch: Partial<TryResultCardState>): Partial<TryResultCardState> {
  const normalized: Partial<TryResultCardState> = { ...patch };

  if ('title' in patch) {
    normalized.title = normalizeOptionalText(patch.title);
  }
  if ('sourceLabel' in patch) {
    normalized.sourceLabel = normalizeOptionalText(patch.sourceLabel);
  }
  if ('shareUrl' in patch) {
    normalized.shareUrl = normalizeOptionalText(patch.shareUrl);
  }
  if ('errorMessage' in patch) {
    normalized.errorMessage = normalizeOptionalText(patch.errorMessage);
  }

  return normalized;
}

function normalizeOptionalText(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function renderStore(): void {
  const host = getResultsHost();
  if (!host) {
    return;
  }

  while (host.firstChild) {
    host.removeChild(host.firstChild);
  }

  for (const card of store.cards) {
    host.appendChild(renderCard(card));
  }
}

function getResultsHost(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.getElementById(ResultsDom.HOST_ID);
}

function renderCard(card: TryResultCardState): HTMLElement {
  const article = document.createElement('article');
  article.className = `${ResultsDom.CARD_CLASS} is-${card.status}`;
  article.id = card.id;
  article.setAttribute('data-status', card.status);

  const content = document.createElement('div');
  content.className = 'try-result-content';

  const eyebrow = document.createElement('div');
  eyebrow.className = 'try-result-eyebrow';
  eyebrow.textContent = card.sourceLabel ?? sourceLabelForCard(card);
  content.appendChild(eyebrow);

  const title = document.createElement('div');
  title.className = 'try-result-title';
  title.textContent = card.title ?? titleForCard(card);
  content.appendChild(title);

  const status = document.createElement('span');
  status.className = ResultsDom.STATUS_CLASS;
  status.textContent = card.errorMessage ?? card.statusMessage;
  if (shouldRenderLiveStatus(card)) {
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
  }
  content.appendChild(status);

  article.appendChild(content);

  const action = renderCardAction(card);
  if (action) {
    article.appendChild(action);
  }

  return article;
}

function renderCardAction(card: TryResultCardState): HTMLElement | null {
  if (card.status !== 'ready' || !card.shareUrl) {
    return null;
  }

  const link = document.createElement('a');
  link.className = ResultsDom.OPEN_CLASS;
  link.href = card.shareUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.setAttribute('aria-label', 'Open Gist in new tab');

  const wrap = document.createElement('span');
  wrap.className = 'try-result-open-wrap';
  wrap.setAttribute('aria-hidden', 'true');

  const iconBox = document.createElement('span');
  iconBox.className = 'try-result-open-icon';

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M7 17L17 7M9 7h8v8');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '2');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  icon.appendChild(path);
  iconBox.appendChild(icon);

  const label = document.createElement('span');
  label.className = 'try-result-open-label';
  label.textContent = 'open';

  wrap.appendChild(iconBox);
  wrap.appendChild(label);
  link.appendChild(wrap);
  return link;
}

function shouldRenderLiveStatus(card: TryResultCardState): boolean {
  if (store.activeCardId === card.id && ActiveStatuses.has(card.status)) {
    return true;
  }
  return terminalAnnouncementCardId === card.id;
}

function shouldAnnounceTerminalTransition(
  previousState: TryResultCardState,
  nextState: TryResultCardState
): boolean {
  return store.activeCardId === nextState.id
    && ActiveStatuses.has(previousState.status)
    && !ActiveStatuses.has(nextState.status);
}

function scheduleTerminalAnnouncement(id: string): void {
  clearTerminalAnnouncement();
  terminalAnnouncementCardId = id;
  terminalAnnouncementTimerId = setTimeout(() => {
    if (terminalAnnouncementCardId === id) {
      terminalAnnouncementCardId = null;
      renderStore();
    }
    terminalAnnouncementTimerId = null;
  }, TERMINAL_ANNOUNCEMENT_MS);
}

function clearTerminalAnnouncement(): void {
  terminalAnnouncementCardId = null;
  if (terminalAnnouncementTimerId !== null) {
    clearTimeout(terminalAnnouncementTimerId);
    terminalAnnouncementTimerId = null;
  }
}

function titleForCard(card: TryResultCardState): string {
  if (card.status === 'ready') {
    return 'Your Gist is ready';
  }
  if (card.status === 'error') {
    return 'Could not create this Gist';
  }
  return 'Creating your Gist';
}

function sourceLabelForCard(card: TryResultCardState): string {
  if (card.shareUrl) {
    try {
      return new URL(card.shareUrl).hostname;
    } catch {
      return 'Gist';
    }
  }
  return 'Gist';
}
